<template>
  <div
    class="teacher-signature-page"
    :class="{ 'teacher-signature-page--virtual-landscape': isVirtualLandscape }"
    :style="virtualLandscapeStyle"
  >
    <van-nav-bar :title="navBarTitle" :border="false" />

    <div v-if="loading" class="page-state">
      <van-loading color="#3b92f5" size="28px">正在加载签名任务...</van-loading>
    </div>

    <div v-else-if="errorMessage" class="page-state page-state--error">
      <van-icon name="warning-o" class="page-state__icon" />
      <p class="page-state__title">暂时无法签名</p>
      <p class="page-state__message">{{ errorMessage }}</p>
      <van-button v-if="canRetry" plain type="info" size="small" @click="loadTask">重新加载</van-button>
    </div>

    <template v-else>
      <section
        v-if="isOrientationBlocked"
        class="orientation-guide"
        role="status"
        aria-live="polite"
      >
        <div class="orientation-guide__visual" aria-hidden="true">
          <span class="orientation-guide__rotate">↻</span>
          <span class="orientation-guide__phone">
            <i />
          </span>
        </div>
        <h1 class="orientation-guide__title">请横屏签名</h1>
        <p class="orientation-guide__message">请将手机旋转至横屏，页面会自动进入签名状态</p>
      </section>

      <main class="signature-content">
        <section class="signature-card">
          <div class="signature-card__header">
            <h1>手写签名</h1>
            <span v-if="isSubmitted" class="signature-status">已提交</span>
          </div>

          <div class="signature-area">
            <img
              v-if="isSubmitted && signatureImageUrl"
              :src="signatureImageUrl"
              class="signature-result"
              alt="教师签名"
            >
            <div v-else-if="isSubmitted" class="signature-result-placeholder">
              <van-icon name="passed" />
              <span>签名已提交</span>
            </div>
            <signature-canvas
              v-else
              ref="signatureCanvas"
              :disabled="submitting || isOrientationBlocked"
              :landscape-only="true"
              :virtual-landscape="isVirtualLandscape"
              @change="handleSignatureChange"
            />
          </div>

          <div class="signature-card__footer">
            <span>{{ footerHint }}</span>
            <span>{{ signatureStateText }}</span>
          </div>
        </section>
      </main>

      <footer class="signature-actions">
        <van-button
          class="signature-actions__clear"
          :disabled="!hasSignature || submitting || isSubmitted || isOrientationBlocked"
          @click="clearSignature"
        >清除</van-button>
        <van-button
          class="signature-actions__submit"
          type="info"
          :loading="submitting"
          loading-text="提交中..."
          :disabled="!hasSignature || isSubmitted || isOrientationBlocked"
          @click="submitSignature"
        >{{ isSubmitted ? '已提交' : '提交签名' }}</van-button>
      </footer>
    </template>
  </div>
</template>

<script>
import { Dialog, Toast } from 'vant'
import SignatureCanvas from '@/components/SignatureCanvas'
import {
  getTeacherSignatureTask,
  submitTeacherSignature
} from '@/api/teacherSignature'

const TASK_STATUS = {
  WAITING: 'WAITING',
  SUBMITTED: 'SUBMITTED',
  EXPIRED: 'EXPIRED'
}

function getViewportSize() {
  const visualViewport = window.visualViewport
  return {
    width: (visualViewport && visualViewport.width) || window.innerWidth || document.documentElement.clientWidth,
    height: (visualViewport && visualViewport.height) || window.innerHeight || document.documentElement.clientHeight
  }
}

function isWeChatBrowser() {
  return /micromessenger|wxwork/i.test(window.navigator.userAgent || '')
}

export default {
  name: 'TeacherSignature',
  components: {
    SignatureCanvas
  },
  data() {
    const viewport = getViewportSize()
    return {
      ticket: '',
      taskStatus: TASK_STATUS.WAITING,
      signatureUrl: '',
      localPreviewUrl: '',
      loading: true,
      submitting: false,
      hasSignature: false,
      errorMessage: '',
      canRetry: false,
      isLandscape: viewport.width > viewport.height,
      viewportTimer: null,
      viewportWidth: viewport.width,
      viewportHeight: viewport.height,
      wechatBrowser: isWeChatBrowser(),
      scrollLockApplied: false,
      previousHtmlOverflow: '',
      previousBodyOverflow: ''
    }
  },
  computed: {
    isSubmitted() {
      return this.taskStatus === TASK_STATUS.SUBMITTED
    },
    navBarTitle() {
      return this.isVirtualLandscape && !this.isSubmitted ? '请横向拿手机签名' : '签名确认'
    },
    isVirtualLandscape() {
      return this.wechatBrowser && !this.isLandscape
    },
    virtualLandscapeStyle() {
      if (!this.isVirtualLandscape) {
        return {}
      }
      return {
        '--virtual-landscape-width': `${this.viewportHeight}px`,
        '--virtual-landscape-height': `${this.viewportWidth}px`
      }
    },
    signatureImageUrl() {
      if (this.localPreviewUrl) {
        return this.localPreviewUrl
      }
      if (!this.signatureUrl) {
        return ''
      }
      if (/^(https?:|data:|blob:)/i.test(this.signatureUrl)) {
        return this.signatureUrl
      }
      return `${this.imgUrl}${this.signatureUrl.replace(/^\//, '')}`
    },
    footerHint() {
      return this.isSubmitted ? '请返回电脑端获取签名并完成保存' : '签名完成后点击提交'
    },
    signatureStateText() {
      if (this.isSubmitted) {
        return '已提交'
      }
      return this.hasSignature ? '已签名，待提交' : '未签名'
    },
    isOrientationBlocked() {
      return !this.isLandscape && !this.isVirtualLandscape && !this.isSubmitted
    }
  },
  watch: {
    isVirtualLandscape(value) {
      this.updateScrollLock(value)
      this.$nextTick(() => {
        this.refreshSignatureCanvas()
      })
    }
  },
  created() {
    document.title = '签名确认'
    this.ticket = String(this.$route.query.ticket || '').trim()
    if (!this.ticket) {
      this.loading = false
      this.errorMessage = '二维码参数缺失，请返回电脑端重新打开扫码签名。'
      return
    }
    this.loadTask()
  },
  mounted() {
    this.syncViewportOrientation()
    this.updateScrollLock(this.isVirtualLandscape)
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('orientationchange', this.handleViewportChange)
    window.addEventListener('pageshow', this.handleViewportChange)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleViewportChange)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('orientationchange', this.handleViewportChange)
    window.removeEventListener('pageshow', this.handleViewportChange)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportChange)
    }
    clearTimeout(this.viewportTimer)
    this.updateScrollLock(false)
    this.revokeLocalPreview()
  },
  methods: {
    handleViewportChange() {
      this.syncViewportOrientation()
      this.updateScrollLock(this.isVirtualLandscape)
      clearTimeout(this.viewportTimer)
      this.viewportTimer = setTimeout(() => {
        this.syncViewportOrientation()
        this.updateScrollLock(this.isVirtualLandscape)
        this.$nextTick(() => {
          this.refreshSignatureCanvas()
        })
      }, 180)
    },
    syncViewportOrientation() {
      const viewport = getViewportSize()
      this.viewportWidth = viewport.width
      this.viewportHeight = viewport.height
      this.isLandscape = viewport.width > viewport.height
    },
    updateScrollLock(locked) {
      if (locked && !this.scrollLockApplied) {
        this.previousHtmlOverflow = document.documentElement.style.overflow
        this.previousBodyOverflow = document.body.style.overflow
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
        this.scrollLockApplied = true
        return
      }
      if (!locked && this.scrollLockApplied) {
        document.documentElement.style.overflow = this.previousHtmlOverflow
        document.body.style.overflow = this.previousBodyOverflow
        this.scrollLockApplied = false
      }
    },
    refreshSignatureCanvas() {
      if ((!this.isLandscape && !this.isVirtualLandscape) || !this.$refs.signatureCanvas) {
        return
      }
      this.$nextTick(() => {
        if (this.$refs.signatureCanvas) {
          this.$refs.signatureCanvas.refresh()
        }
      })
    },
    async loadTask() {
      this.loading = true
      this.errorMessage = ''
      this.canRetry = false
      try {
        const response = await getTeacherSignatureTask(this.ticket)
        const task = response.data || {}
        const status = this.normalizeStatus(task.status)
        if (status === TASK_STATUS.EXPIRED) {
          this.errorMessage = '二维码已过期，请返回电脑端重新生成二维码。'
          return
        }
        this.taskStatus = status
        this.signatureUrl = task.signatureUrl || ''
      } catch (error) {
        this.errorMessage = this.resolveErrorMessage(error)
        this.canRetry = true
      } finally {
        this.loading = false
      }
    },
    normalizeStatus(status) {
      if (status === 1 || String(status).toUpperCase() === TASK_STATUS.SUBMITTED) {
        return TASK_STATUS.SUBMITTED
      }
      if (status === 2 || String(status).toUpperCase() === TASK_STATUS.EXPIRED) {
        return TASK_STATUS.EXPIRED
      }
      return TASK_STATUS.WAITING
    },
    handleSignatureChange(hasSignature) {
      this.hasSignature = hasSignature
    },
    clearSignature() {
      this.$refs.signatureCanvas.clear()
    },
    async submitSignature() {
      if (!this.hasSignature || this.submitting) {
        return
      }
      this.submitting = true
      try {
        const blob = await this.$refs.signatureCanvas.getSignatureBlob()
        const formData = new FormData()
        formData.append('file', blob, 'teacher-signature.png')
        const response = await submitTeacherSignature(this.ticket, formData)
        const result = response.data || {}
        this.revokeLocalPreview()
        this.localPreviewUrl = window.URL.createObjectURL(blob)
        this.signatureUrl = result.signatureUrl || ''
        this.taskStatus = TASK_STATUS.SUBMITTED
        this.hasSignature = false
        await Dialog.alert({
          title: '提交成功',
          message: '签名已提交，请返回电脑端获取签名图片并完成保存。',
          confirmButtonText: '知道了',
          confirmButtonColor: '#3b92f5',
          getContainer: this.isVirtualLandscape ? () => this.$el : 'body'
        })
      } catch (error) {
        Toast.fail({
          message: this.resolveErrorMessage(error, '签名提交失败，请重试'),
          getContainer: this.isVirtualLandscape ? () => this.$el : 'body'
        })
      } finally {
        this.submitting = false
      }
    },
    revokeLocalPreview() {
      if (!this.localPreviewUrl) {
        return
      }
      window.URL.revokeObjectURL(this.localPreviewUrl)
      this.localPreviewUrl = ''
    },
    resolveErrorMessage(error, fallback) {
      if (error && error.message) {
        return error.message
      }
      if (typeof error === 'string' && error !== 'error') {
        return error
      }
      return fallback || '签名任务加载失败，请稍后重试。'
    }
  }
}
</script>

<style scoped lang="scss">
.teacher-signature-page {
  min-height: 100vh;
  background: #eef5fb;
  color: #2f2f46;
}

.orientation-guide {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px calc(32px + env(safe-area-inset-bottom));
  text-align: center;
  background: #eef5fb;
}

.orientation-guide__visual {
  position: relative;
  width: 116px;
  height: 86px;
  margin-bottom: 22px;
}

.orientation-guide__rotate {
  position: absolute;
  top: -10px;
  right: 2px;
  color: #83baf6;
  font-size: 46px;
  font-weight: 300;
  line-height: 1;
}

.orientation-guide__phone {
  position: absolute;
  bottom: 4px;
  left: 8px;
  width: 84px;
  height: 48px;
  border: 3px solid #3b92f5;
  border-radius: 10px;
  box-shadow: 0 10px 22px rgba(59, 146, 245, 0.16);

  &::before {
    position: absolute;
    top: 50%;
    left: 6px;
    width: 3px;
    height: 12px;
    background: #9bc7f7;
    border-radius: 3px;
    transform: translateY(-50%);
    content: '';
  }

  i {
    position: absolute;
    top: 50%;
    right: 5px;
    width: 5px;
    height: 5px;
    border: 1px solid #9bc7f7;
    border-radius: 50%;
    transform: translateY(-50%);
  }
}

.orientation-guide__title {
  margin: 0;
  color: #2f2f46;
  font-size: 20px;
  font-weight: 600;
}

.orientation-guide__message {
  margin: 12px 0 0;
  color: #8c96a0;
  font-size: 14px;
  line-height: 22px;
}

::v-deep .van-nav-bar {
  height: 44px;
  background: #ffffff;
}

::v-deep .van-nav-bar__title {
  color: #222222;
  font-size: 16px;
  font-weight: 500;
}

.signature-content {
  padding: 12px 16px 94px;
}

.signature-card {
  padding: 0 12px 10px;
  background: #ffffff;
  border-radius: 8px;
}

.signature-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 46px;

  h1 {
    margin: 0;
    color: #2f2f46;
    font-size: 16px;
    font-weight: 600;
  }
}

.signature-status {
  padding: 3px 10px;
  color: #45a722;
  font-size: 12px;
  background: #edf8e8;
  border-radius: 12px;
}

.signature-area {
  height: calc(100vh - 184px);
  min-height: 420px;
  max-height: 720px;
}

.signature-result,
.signature-result-placeholder {
  width: 100%;
  height: 100%;
  background: #fbfdff;
  border: 1px dashed #bfd1e6;
  border-radius: 4px;
}

.signature-result {
  padding: 24px;
  object-fit: contain;
}

.signature-result-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #65b63d;
  font-size: 15px;

  .van-icon {
    font-size: 42px;
  }
}

.signature-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  color: #9aa3ad;
  font-size: 12px;
}

.signature-actions {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  gap: 10px;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -2px 12px rgba(45, 71, 96, 0.06);
}

.signature-actions__clear {
  flex: 0 0 88px;
  color: #7a7c8b;
  border-color: #dce2e8;
}

.signature-actions__submit {
  flex: 1;
  background: #3b92f5;
  border-color: #3b92f5;
}

.signature-actions__clear,
.signature-actions__submit {
  height: 42px;
  border-radius: 4px;
  font-size: 15px;
}

.page-state {
  display: flex;
  min-height: calc(100vh - 44px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: #7a7c8b;
}

.page-state--error {
  text-align: center;
}

.page-state__icon {
  color: #f3a632;
  font-size: 48px;
}

.page-state__title {
  margin-top: 16px;
  color: #2f2f46;
  font-size: 18px;
  font-weight: 600;
}

.page-state__message {
  margin: 10px 0 22px;
  color: #8c96a0;
  font-size: 14px;
  line-height: 22px;
}

@media screen and (orientation: landscape) {
  .teacher-signature-page {
    height: 100vh;
    min-height: 0;
    overflow: hidden;
  }

  ::v-deep .van-nav-bar {
    height: 40px;
  }

  ::v-deep .van-nav-bar__content {
    height: 40px;
  }

  .signature-content {
    height: calc(100vh - 40px);
    padding: 8px calc(12px + env(safe-area-inset-right)) calc(62px + env(safe-area-inset-bottom)) calc(12px + env(safe-area-inset-left));
  }

  .signature-card {
    display: flex;
    height: 100%;
    flex-direction: column;
    padding: 0 10px 6px;
  }

  .signature-card__header {
    height: 34px;
    flex: 0 0 34px;

    h1 {
      font-size: 14px;
    }
  }

  .signature-area {
    height: auto;
    min-height: 0;
    max-height: none;
    flex: 1;
  }

  .signature-card__footer {
    min-height: 28px;
    flex: 0 0 28px;
  }

  .signature-actions {
    padding: 7px calc(12px + env(safe-area-inset-right)) calc(7px + env(safe-area-inset-bottom)) calc(12px + env(safe-area-inset-left));
  }

  .signature-actions__clear,
  .signature-actions__submit {
    height: 38px;
    font-size: 14px;
  }
}

/*
 * Some embedded browsers (notably WeChat WebView) keep the viewport in
 * portrait mode even when the user turns the phone sideways.  The page is
 * laid out with the viewport dimensions swapped, then rotated around its
 * top-left corner to provide a full-screen landscape canvas.  SignaturePad
 * converts the visual touch coordinates back to the untransformed canvas in
 * SignatureCanvas.
 */
.teacher-signature-page--virtual-landscape {
  position: fixed;
  top: 0;
  left: 0;
  width: var(--virtual-landscape-width, 100vh);
  height: var(--virtual-landscape-height, 100vw);
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  transform: rotate(90deg) translateY(-100%);
  transform-origin: top left;
}

.teacher-signature-page--virtual-landscape ::v-deep .van-nav-bar,
.teacher-signature-page--virtual-landscape ::v-deep .van-nav-bar__content {
  height: 40px;
}

.teacher-signature-page--virtual-landscape .page-state {
  min-height: calc(100% - 40px);
}

.teacher-signature-page--virtual-landscape .signature-content {
  height: calc(100% - 40px);
  padding: 8px 12px calc(62px + env(safe-area-inset-bottom)) 12px;
  box-sizing: border-box;
}

.teacher-signature-page--virtual-landscape .signature-card {
  display: flex;
  height: 100%;
  flex-direction: column;
  padding: 0 10px 6px;
  box-sizing: border-box;
}

.teacher-signature-page--virtual-landscape .signature-card__header {
  height: 34px;
  flex: 0 0 34px;
}

.teacher-signature-page--virtual-landscape .signature-card__header h1 {
  font-size: 14px;
}

.teacher-signature-page--virtual-landscape .signature-area {
  height: auto;
  min-height: 0;
  max-height: none;
  flex: 1;
}

.teacher-signature-page--virtual-landscape .signature-card__footer {
  min-height: 28px;
  flex: 0 0 28px;
}

.teacher-signature-page--virtual-landscape .signature-actions {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 7px 12px calc(7px + env(safe-area-inset-bottom)) 12px;
}

.teacher-signature-page--virtual-landscape .signature-actions__clear,
.teacher-signature-page--virtual-landscape .signature-actions__submit {
  height: 38px;
  font-size: 14px;
}
</style>
